import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useEffect, useMemo, useRef, useState } from "react"
import { useSheets } from "../../../api/questionnaireProcessingApi"
import { columnIndexToRef } from "../../master-data/mappingUtils"
import { SectionLabel } from "../../shared/SectionLabel"

export interface SheetCellClick {
  sheetName: string
  header: string
  customerArticleNumber: string | null
  itemId: string | null
}

interface SheetPreviewPanelProps {
  questionnaireId: string
  activeSheetName?: string
  highlightHeader?: string | null
  highlightArticleNumber?: string | null
  /** Fired when an answer-target cell is clicked — omit to leave cells unclickable. */
  onCellClick?: (cell: SheetCellClick) => void
}

export function SheetPreviewPanel({
  questionnaireId,
  activeSheetName,
  highlightHeader,
  highlightArticleNumber,
  onCellClick,
}: SheetPreviewPanelProps) {
  const { translate } = useTranslation()
  const { data, isLoading } = useSheets(questionnaireId)
  const sheets = data?.sheets ?? []
  // Tracks which `activeSheetName` the manual tab pick was made under, so a
  // later prop change (e.g. clicking a column on a different sheet in
  // MappingStep/GapsStep) supersedes a stale pick instead of being stuck
  // forever on whatever sheet the user last clicked.
  const [manualSheet, setManualSheet] = useState<{
    for: string | undefined
    name: string
  } | null>(null)
  const selectedSheetName =
    manualSheet && manualSheet.for === activeSheetName
      ? manualSheet.name
      : activeSheetName
  const sheet = sheets.find((s) => s.name === selectedSheetName) ?? sheets[0]

  // `highlightHeader` is only meaningful against the sheet it came from — a
  // header name can repeat across sheets, so this only ever resolves within
  // the currently active `sheet`, never bleeding into another one.
  const cellRef = useMemo(() => {
    if (!sheet || !highlightHeader) return null
    const colIndex = sheet.headers.indexOf(highlightHeader)
    const rowIndex = highlightArticleNumber
      ? sheet.rows.findIndex(
          (r) => r.customerArticleNumber === highlightArticleNumber,
        )
      : -1
    return { colIndex, rowIndex }
  }, [sheet, highlightHeader, highlightArticleNumber])

  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!cellRef || cellRef.colIndex < 0) return
    const selector =
      cellRef.rowIndex >= 0
        ? `[data-row="${cellRef.rowIndex}"][data-col="${cellRef.colIndex}"]`
        : `[data-header-col="${cellRef.colIndex}"]`
    containerRef.current?.querySelector(selector)?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    })
  }, [cellRef])

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <SectionLabel>
          {cellRef && cellRef.colIndex >= 0
            ? `${sheet?.name}!${columnIndexToRef(cellRef.colIndex)}${cellRef.rowIndex >= 0 ? cellRef.rowIndex + 2 : ""}`
            : (sheet?.name ?? "")}
        </SectionLabel>
      </div>

      <div ref={containerRef} className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-primary" />
          </div>
        ) : !sheet || sheet.rows.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
            {translate("MC_QP_SHEET_NO_DATA")}
          </div>
        ) : (
          <Table containerClassName="overflow-visible" className="text-xs">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10 bg-muted/40" />
                {sheet.headers.map((header, i) => {
                  const isAnswerHeader = sheet.answerHeaders.includes(header)
                  return (
                    <TableHead
                      key={header + i}
                      data-header-col={i}
                      onClick={() =>
                        isAnswerHeader &&
                        onCellClick?.({
                          sheetName: sheet.name,
                          header,
                          customerArticleNumber: null,
                          itemId: null,
                        })
                      }
                      className={cn(
                        "bg-muted/40 font-medium text-muted-foreground",
                        isAnswerHeader &&
                          onCellClick &&
                          "cursor-pointer hover:bg-primary/10",
                        cellRef?.colIndex === i && "bg-primary/15 text-primary",
                      )}
                    >
                      {header}
                    </TableHead>
                  )
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sheet.rows.map((row, rowIndex) => (
                <TableRow key={row.itemId ?? rowIndex}>
                  <TableCell className="font-mono text-center text-muted-foreground/70">
                    {rowIndex + 2}
                  </TableCell>
                  {row.cells.map((cell, colIndex) => {
                    const header = sheet.headers[colIndex]
                    const isAnswerCell = sheet.answerHeaders.includes(header)
                    const isColHighlighted = cellRef?.colIndex === colIndex
                    const isCellHighlighted =
                      isColHighlighted && cellRef?.rowIndex === rowIndex
                    return (
                      <TableCell
                        key={colIndex}
                        data-row={rowIndex}
                        data-col={colIndex}
                        onClick={() =>
                          isAnswerCell &&
                          onCellClick?.({
                            sheetName: sheet.name,
                            header,
                            customerArticleNumber: row.customerArticleNumber,
                            itemId: row.itemId,
                          })
                        }
                        className={cn(
                          isAnswerCell &&
                            onCellClick &&
                            "cursor-pointer hover:bg-primary/10",
                          isColHighlighted && "bg-primary/10",
                          isCellHighlighted && "bg-primary/20",
                        )}
                      >
                        {cell}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {sheets.length > 1 && (
        <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-t px-2 py-1.5">
          {sheets.map((s) => (
            <button
              key={s.name}
              type="button"
              onClick={() =>
                setManualSheet({ for: activeSheetName, name: s.name })
              }
              className={cn(
                "shrink-0 rounded px-2 py-1 text-[11px]",
                s.name === sheet?.name
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:bg-muted/50",
              )}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
